import { type OfferExpiryRow, offerConfirmExpiry } from '@repo/sdk/gen'
import { Alert, AlertDescription } from '@repo/ui/alert'
import { Badge } from '@repo/ui/badge'
import { Button } from '@repo/ui/button'
import { Skeleton } from '@repo/ui/skeleton'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { toast } from 'sonner'
import { useTranslation } from '@/lib/i18n'
import { makeQueryKeys } from '@/query/query-keys'
import { ConfirmDialog } from '@/routes/(private)/-components/confirm-dialog'
import { WhenWritable } from '../../../-components/when-writable'
import { useWorkingCall } from '../../../-hooks/use-working-call'
import { formatRomeDeadline, formatRomeMoment } from '../../../-lib/rome-time'
import { RecordReplyDialog } from '../-components/record-reply-dialog'
import { offerExpiriesQueryOptions } from '../-query/query-options'

export const Route = createFileRoute(
	'/(private)/bando/$residenceId/offerte/decadenze/'
)({
	component: ExpiriesPage,
	staticData: {
		breadcrumb: 'admin-offers:expiries.title'
	}
})

/**
 * The offers whose term ran out with no answer (FFD 2.11, DESIGN.md case 12):
 * the manager first looks in the PEC of the call, then confirms the lapse or
 * records the answer received in time, with the message as proof. The lapse
 * is never automatic.
 */
function ExpiriesPage() {
	const { t } = useTranslation()
	const { residenceId } = Route.useParams()
	const { callId } = useWorkingCall()
	const query = useQuery(offerExpiriesQueryOptions(residenceId, callId))
	return (
		<div className='mx-auto flex w-full max-w-5xl flex-col gap-6 p-4 md:p-6'>
			<header className='flex flex-col gap-3'>
				<h1 className='font-semibold text-xl'>
					{t('admin-offers:expiries.title')}
				</h1>
				<Alert>
					<AlertDescription>
						{t('admin-offers:expiries.reminder')}
					</AlertDescription>
				</Alert>
			</header>
			{query.isLoading && <Skeleton className='h-48 w-full' />}
			{query.isError && (
				<p className='text-destructive text-sm'>
					{t('admin-offers:errors.default')}
				</p>
			)}
			{query.data?.length === 0 && (
				<p className='text-muted-foreground text-sm'>
					{t('admin-offers:expiries.empty')}
				</p>
			)}
			{query.data && query.data.length > 0 && (
				<ul className='flex flex-col divide-y divide-border rounded-xl border border-border'>
					{query.data.map((row) => (
						<ExpiryItem
							key={row.id}
							residenceId={residenceId}
							row={row}
						/>
					))}
				</ul>
			)}
		</div>
	)
}

function ExpiryItem({
	residenceId,
	row
}: {
	residenceId: string
	row: OfferExpiryRow
}) {
	const { t, i18n } = useTranslation()
	const lastDay = formatRomeDeadline(
		new Date(Date.parse(row.expiresAt) - 1).toISOString(),
		i18n.language
	).day
	const late =
		row.confirmDueAt !== null && Date.parse(row.confirmDueAt) <= Date.now()
	return (
		<li className='flex flex-col gap-3 p-4 md:flex-row md:items-start md:justify-between'>
			<div className='flex min-w-0 flex-col gap-1 text-sm'>
				<div className='flex flex-wrap items-baseline gap-2'>
					<Link
						className='font-medium hover:underline'
						params={{ residenceId, submissionId: row.submissionId }}
						to='/bando/$residenceId/domande/$submissionId'
					>
						{row.lastName} {row.firstName}
					</Link>
					<span className='text-muted-foreground tabular-nums'>
						{t('admin-offers:columns.protocol')} {row.protocol}
					</span>
					<Badge variant='warning'>
						{t('admin-offers:state.expiry_proposed')}
					</Badge>
				</div>
				<span className='text-muted-foreground'>
					{t('admin-offers:expiries.sent', {
						date: formatRomeMoment(row.sentAt, i18n.language)
					})}{' '}
					·{' '}
					{row.lastDeliveryFailed
						? t('admin-offers:expiries.not_delivered')
						: t('admin-offers:expiries.delivered')}
				</span>
				<span className='text-muted-foreground'>
					{t('admin-offers:expiries.expired', { day: lastDay })}
				</span>
				{row.confirmDueAt && (
					<span className='flex flex-wrap items-center gap-2'>
						{t('admin-offers:expiries.due', {
							date: formatRomeMoment(
								row.confirmDueAt,
								i18n.language
							)
						})}
						{late && (
							<Badge variant='warning'>
								{t('admin-offers:expiries.late')}
							</Badge>
						)}
					</span>
				)}
				{row.lateReplies.map((reply) => (
					<span
						className='flex flex-wrap items-center gap-2 text-muted-foreground'
						key={reply.receivedAt}
					>
						<Badge variant='neutral'>
							{t('admin-offers:expiries.late_reply')}
						</Badge>
						{t('admin-offers:expiries.late_reply_text', {
							decision: t(
								`admin-offers:decision.${reply.decision}`
							),
							date: formatRomeMoment(
								reply.receivedAt,
								i18n.language
							)
						})}
					</span>
				))}
			</div>
			<WhenWritable>
				<div className='flex shrink-0 flex-wrap gap-2'>
					<RecordReplyDialog
						offerId={row.id}
						residenceId={residenceId}
					/>
					<ConfirmExpiryButton
						offerId={row.id}
						residenceId={residenceId}
					/>
				</div>
			</WhenWritable>
		</li>
	)
}

function ConfirmExpiryButton({
	residenceId,
	offerId
}: {
	residenceId: string
	offerId: string
}) {
	const { t } = useTranslation()
	const queryClient = useQueryClient()
	const confirm = useMutation({
		mutationFn: () =>
			offerConfirmExpiry({ body: { data: { residenceId, offerId } } }),
		onSuccess: () =>
			toast.success(t('admin-offers:expiries.confirm.success')),
		onError: () => toast.error(t('admin-offers:expiries.confirm.error')),
		onSettled: () =>
			queryClient.invalidateQueries({
				queryKey: makeQueryKeys.offers.all()
			})
	})
	return (
		<ConfirmDialog
			confirmLabel={t('admin-offers:expiries.confirm.confirm')}
			description={t('admin-offers:expiries.confirm.description')}
			onConfirm={(settled) =>
				confirm.mutate(undefined, { onSettled: settled })
			}
			pending={confirm.isPending}
			title={t('admin-offers:expiries.confirm.title')}
			trigger={
				<Button variant='outline'>
					{t('admin-offers:expiries.confirm.command')}
				</Button>
			}
		/>
	)
}
